/**
 * @author      luis.orihuela@zumper.com (Luis Orihuela)
 * @copyright   Copyright (c) 2023, Zumper
 * @description SpecialtyHousingConfirmation
 */
import {
  Box,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  useBreakpointValue,
} from '@chakra-ui/react'
import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
} from '@zcode-ui/containers'
import { Button } from '@zcode-ui/controls'
import { Close } from '@zcode-ui/icons'
import { selectEntity } from '@zcode-ui/web-zumper-global-state'
import React from 'react'
import { useSelector } from 'react-redux'
import { IncomeRestrictions } from '../../../detail/Content/IncomeRestrictions/IncomeRestrictions'
import { useMessageFormContext } from '../NewMessageForm/MessageFormContext'

interface SpecialtyHousingConfirmationProps {
  isButtonDisabled?: boolean
  isLoading?: boolean
  onSubmit: () => void
  renderInModal?: boolean
}

export const SpecialtyHousingMessage = ({
  isButtonDisabled,
  isLoading,
  onSubmit,
}: Omit<SpecialtyHousingConfirmationProps, 'isDrawer'>) => {
  const entity = useSelector(selectEntity)
  const { isIncomeRestricted, incomeRestrictions } = entity ?? {}
  const restrictions =
    incomeRestrictions && 'restrictions' in incomeRestrictions
      ? incomeRestrictions.restrictions
      : undefined
  const { message } = useMessageFormContext()
  return (
    <Box<'div'>>
      {isIncomeRestricted && restrictions?.length ? (
        <IncomeRestrictions hideHeading isShortRow restrictions={restrictions} />
      ) : (
        message
      )}

      <Button
        className="mb-4 mt-6 w-full"
        disabled={isButtonDisabled}
        isLoading={isLoading}
        onClick={onSubmit}
        size="lg"
        type="submit"
        variant="solid"
      >
        Acknowledge and submit
      </Button>
    </Box>
  )
}

interface ModalOrDrawerProps {
  children: React.ReactNode
  heading: string
  isOpen: boolean
  onClose: () => void
}

export const SpecialtyHousingDrawer = ({
  children,
  heading,
  isOpen,
  onClose,
}: ModalOrDrawerProps) => {
  return (
    <Drawer
      customSize="fit-content"
      isOpen={isOpen}
      onClose={onClose}
      placement="bottom"
      returnFocusOnClose
    >
      <DrawerOverlay zIndex={1400} />
      <DrawerContent borderTopRadius="xl" sx={{ header: { padding: 6 } }} zIndex={1500}>
        <DrawerHeader
          borderBottomColor="neutral.100"
          borderBottomStyle="solid"
          borderBottomWidth="1px"
          position="relative"
        >
          <Button
            className="absolute left-3 top-4"
            data-testid="close-drawer"
            onClick={onClose}
            tabIndex={2}
            variant="ghost"
          >
            <Close fill="var(--chakra-colors-gray-600)" size={10} />
          </Button>
          {heading}
        </DrawerHeader>
        <DrawerBody flexBasis="auto" padding={6}>
          {children}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  )
}

export const SpecialtyHousingModal = ({
  children,
  heading,
  isOpen,
  onClose,
}: ModalOrDrawerProps) => {
  return (
    <Modal
      isCentered
      size="lg"
      isOpen={isOpen}
      onClose={onClose}
      returnFocusOnClose
      variant="leftClose"
    >
      <ModalOverlay zIndex={1400} />
      <ModalContent borderTopRadius="xl" sx={{ header: { padding: 6 } }} zIndex={1500}>
        <ModalHeader
          borderBottomColor="neutral.100"
          borderBottomStyle="solid"
          borderBottomWidth="1px"
          borderTopRadius={{ base: 'none', sm: 'md' }}
          position="relative"
        >
          <ModalCloseButton>
            <Close fill="var(--chakra-colors-gray-600)" />
          </ModalCloseButton>
          <Heading>{heading}</Heading>
        </ModalHeader>
        <ModalBody flexBasis="auto" maxHeight="85vh" padding={6}>
          {children}
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

export const SpecialtyHousingInterstitialModal = (props: ModalOrDrawerProps) => {
  const component = useBreakpointValue({
    base: 'drawer',
    lg: 'modal',
  })
  return component === 'modal' ? (
    <SpecialtyHousingModal {...props} />
  ) : (
    <SpecialtyHousingDrawer {...props} />
  )
}

export const SpecialtyHousingConfirmation = ({
  isButtonDisabled,
  onSubmit,
  renderInModal,
}: SpecialtyHousingConfirmationProps) => {
  const {
    goToNextSpecialtyHousingModal,
    heading,
    isSpecialtyHousingOpen,
    onCloseSpecialtyHousing,
  } = useMessageFormContext()
  if (!isSpecialtyHousingOpen) return null
  const handleSubmit = () => {
    goToNextSpecialtyHousingModal(onSubmit)
  }
  if (renderInModal) {
    return (
      <SpecialtyHousingInterstitialModal
        heading={heading}
        isOpen={isSpecialtyHousingOpen}
        onClose={onCloseSpecialtyHousing}
      >
        <SpecialtyHousingMessage isButtonDisabled={isButtonDisabled} onSubmit={handleSubmit} />
      </SpecialtyHousingInterstitialModal>
    )
  }

  return <SpecialtyHousingMessage isButtonDisabled={isButtonDisabled} onSubmit={handleSubmit} />
}
